"use client";

import React, { createContext, useContext, useMemo } from "react";
import { useParams } from "next/navigation";
import { useFetchEcompage } from "@/apis/queryFunctions/ecompagesFunctions";
import { slug } from "@/types/projectConsts";
import { FooterData } from "@/types/footer";
import defaultFooterData from "@/mockDatas/footerData.json";

export function parseProjectInfo(
  content: string | undefined | null
): FooterData {
  if (!content) return defaultFooterData as FooterData;
  try {
    const clean = content
      .replace(/<[^>]*>/g, "")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&nbsp;/g, " ")
      .trim();

    if (!clean) return defaultFooterData as FooterData;
    const parsed = JSON.parse(clean);

    return {
      ...defaultFooterData,
      ...parsed,
      logo: { ...defaultFooterData.logo, ...(parsed?.logo || {}) },
      company: { ...defaultFooterData.company, ...(parsed?.company || {}) },
      policies: {
        ...defaultFooterData.policies,
        ...(parsed?.policies || {}),
        items: parsed?.policies?.items || defaultFooterData.policies.items,
      },
      factories: {
        ...defaultFooterData.factories,
        ...(parsed?.factories || {}),
        items: parsed?.factories?.items || defaultFooterData.factories.items,
      },
      socials: {
        ...defaultFooterData.socials,
        ...(parsed?.socials || {}),
      },
      contactForm: {
        ...defaultFooterData.contactForm,
        ...(parsed?.contactForm || {}),
      },
      certification: {
        ...defaultFooterData.certification,
        ...(parsed?.certification || {}),
      },
    } as FooterData;
  } catch (error) {
    console.error("Error parsing projectInfo JSON from content:", error);
    return defaultFooterData as FooterData;
  }
}

interface ProjectInfoContextType {
  projectInfo: FooterData;
  homepageData: any;
  isLoading: boolean;
}

const ProjectInfoContext = createContext<ProjectInfoContextType>({
  projectInfo: defaultFooterData as FooterData,
  homepageData: null,
  isLoading: false,
});

export function ProjectInfoProvider({
  children,
  initialData,
}: {
  children: React.ReactNode;
  initialData?: any;
}) {
  const params = useParams();
  const locale = (params?.locale as string) || "vi";

  // Fetch homepage data if on other pages where initialData is not provided
  const { data: fetchedData, isLoading } = useFetchEcompage(
    slug.HOMEPAGE,
    locale
  );

  const activeData = initialData || fetchedData;

  const projectInfo = useMemo(() => {
    if (activeData?.content) {
      return parseProjectInfo(activeData.content);
    }
    return defaultFooterData as FooterData;
  }, [activeData?.content]);

  const value = useMemo(
    () => ({
      projectInfo,
      homepageData: activeData,
      isLoading,
    }),
    [projectInfo, activeData, isLoading]
  );

  return (
    <ProjectInfoContext.Provider value={value}>
      {children}
    </ProjectInfoContext.Provider>
  );
}

export function useProjectInfo() {
  const context = useContext(ProjectInfoContext);
  if (!context) {
    return {
      projectInfo: defaultFooterData as FooterData,
      homepageData: null,
      isLoading: false,
    };
  }
  return context;
}

export default ProjectInfoContext;
